import { type FormEvent, useState, useEffect } from "react";
import type { PaperType } from "@markeroo/shared";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { GradeRangeSelector } from "@/components/GradeRangeSelector";
import { Select } from "@/components/Select";
import { fieldLabelClass, inputClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui, placeholders, getPaperTypeLabel, charactersRemaining } from "@/strings";

const PAPER_TYPES: { value: PaperType; label: string }[] = [
  { value: "opinion_argument", label: getPaperTypeLabel("opinion_argument") },
  { value: "informative_explanatory", label: getPaperTypeLabel("informative_explanatory") },
  { value: "narrative", label: getPaperTypeLabel("narrative") },
  { value: "other", label: getPaperTypeLabel("other") },
];

/** Minimal template data needed for edit mode */
interface TemplateEditData {
  name: string;
  paperType?: PaperType | null;
  grades?: number[] | null;
  isDefaultForType?: boolean;
}

interface SaveCriteriaTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    paperType: PaperType | null;
    grades: number[] | null;
    isDefaultForType: boolean;
  }) => void;
  isSubmitting: boolean;
  /** If provided, modal operates in edit mode with pre-populated fields */
  template?: TemplateEditData | null;
  defaultPaperType?: PaperType | null;
  defaultGrades?: number[] | null;
}

export function SaveCriteriaTemplateModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  template,
  defaultPaperType,
  defaultGrades,
}: SaveCriteriaTemplateModalProps) {
  const [name, setName] = useState("");
  const [paperType, setPaperType] = useState<PaperType | null>(null);
  const [grades, setGrades] = useState<number[] | null>(null);
  const [isDefaultForType, setIsDefaultForType] = useState(false);

  const isEditMode = Boolean(template);

  useEffect(() => {
    if (isOpen) {
      if (template) {
        // Edit mode: populate from existing template
        setName(template.name);
        setPaperType(template.paperType ?? null);
        setGrades(template.grades ?? null);
        setIsDefaultForType(template.isDefaultForType ?? false);
      } else {
        // Create mode: reset to defaults
        setName("");
        setPaperType(defaultPaperType ?? null);
        setGrades(defaultGrades ?? null);
        setIsDefaultForType(false);
      }
    }
  }, [isOpen, template, defaultPaperType, defaultGrades]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      paperType,
      grades,
      isDefaultForType,
    });
  };

  const isValid = name.trim().length > 0 && name.trim().length <= 255;

  const modalTitle = isEditMode
    ? ui.headers.editCriteriaTemplateDetails
    : ui.headers.saveCriteriaTemplate;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader title={modalTitle} onClose={onClose} />
      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-4" id="save-criteriaTemplate-form">
          <label className={fieldLabelClass}>
            {ui.labels.criteriaTemplateName}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              required
              maxLength={255}
              placeholder={placeholders.criteriaTemplates.name}
              autoFocus
            />
            {name.length > 240 && (
              <p className="text-xs text-text-theme-muted mt-1">
                {charactersRemaining(255 - name.length)}
              </p>
            )}
          </label>

          <label className={fieldLabelClass}>
            {ui.labels.paperTypeOptional}
            <Select
              value={paperType ?? ""}
              onChange={(e) => setPaperType(e.target.value === "" ? null : (e.target.value as PaperType))}
            >
              <option value="">{ui.labels.anyType}</option>
              {PAPER_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
            <p className="text-xs text-text-theme-muted mt-1">
              {ui.labels.criteriaTemplatePaperTypeHelp}
            </p>
          </label>

          <GradeRangeSelector value={grades} onChange={setGrades} label={ui.labels.gradeRangeOptional} />
          <p className="text-xs text-text-theme-muted -mt-2">
            {ui.labels.chooseGradesForCriteriaTemplate}
          </p>

          {paperType && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefaultForType}
                onChange={(e) => setIsDefaultForType(e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
              <span className="text-sm text-text-theme-primary">
                {ui.labels.setAsDefaultCriteriaTemplate} {PAPER_TYPES.find((t) => t.value === paperType)?.label} {ui.headers.assignments.toLowerCase()}
              </span>
            </label>
          )}
        </form>
      </ModalBody>
      <ModalFooter>
        <button type="button" onClick={onClose} className={buttonVariants.secondary} disabled={isSubmitting}>
          {ui.actions.cancel}
        </button>
        <button
          type="submit"
          form="save-criteriaTemplate-form"
          className={buttonVariants.primary}
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting
            ? ui.actions.saving
            : isEditMode
              ? ui.actions.save
              : `${ui.actions.save} ${ui.labels.criteriaTemplate}`}
        </button>
      </ModalFooter>
    </Modal>
  );
}
